/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { injectIntl, intlShape } from 'react-intl';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import messages from 'src/shared/app/base/l10n/uiL10n';
import styles from './CloseButton.style';
import CrossIcon from 'src/shared/ui/component/icon/CrossIcon';
import CircledCrossIcon from 'src/shared/ui/component/icon/CircledCrossIcon';

type Props = {
  small?: boolean,
  circled?: boolean,
  layoutClass?: string,
  forceBlur?: boolean,
  isTrimmed?: boolean,
  tabIndex: number,
  onClick: (MouseEvent) => void,
  ariaLabel?: string,
  autoFocus?: boolean,
  // Self passed
  intl: intlShape,
} & HTMLButtonElement;

const useStyles = createUseThemeStyles(styles);

const CloseButton = ({
  small,
  circled,
  layoutClass,
  isTrimmed,
  onClick,
  forceBlur,
  ariaLabel,
  autoFocus,
  intl,
  ...props
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();

    onClick(e);

    if (forceBlur) {
      const { current: rootElt } = rootRef;

      if (rootElt) {
        rootElt.blur();
      }
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles();

  const rootClasses = classNames(classes.root, layoutClass, {
    [classes['root-isTrimmed']]: isTrimmed,
  });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const rootRef = React.createRef();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */
  const IconComponent = circled ? CircledCrossIcon : CrossIcon;

  return (
    <button
      {...props}
      className={rootClasses}
      onClick={handleClick}
      type="button"
      ref={rootRef}
      autoFocus={autoFocus}
      aria-label={ariaLabel || intl.formatMessage(messages.close)}
    >
      <IconComponent large={!small} />
    </button>
  );
};

export default compose(React.memo, injectIntl)(CloseButton);
